/**
 * A customer's saved quotes, and one quote in full.
 *
 * Revisions share a quote number, so the list shows the current one with its
 * earlier versions folded underneath rather than six near-identical rows. A
 * superseded revision is still openable: somebody holding that PDF is entitled
 * to see what it said.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import { CartItem } from "../common/models/cart";
import { canonicalizePriceUnit } from "../components/InventorySearch/helpers";
import { convertPriceValueAndUnit } from "../components/InventorySearch/price-helpers";
import { reopenInCart } from "./reopen";
import { ActivityTrail, actorLabel } from "../components/ActivityTrail";
import ConvertToPoDialog from "./ConvertToPoDialog";
import { QuoteSourcingSection } from "./QuoteSourcing";
import { OperatorQuoteEditor } from "./OperatorQuoteEditor";
import { OperatorFreightPanel } from "./OperatorFreightPanel";
import { BetterPriceBadge } from "./BetterPriceBadge";
import { OperatorEmailPanel } from "./OperatorEmailPanel";
import {
  Quote,
  QuoteFlowConfig,
  QuoteSummary,
  deleteQuote,
  downloadQuotePdf,
  emailQuote,
  listQuotes,
  loadQuote,
  loadQuoteFlowConfig,
  markQuoteSentToCustomer,
  messageOf,
  shareQuote,
} from "./api";

const money = (amount: number, currency: string) =>
  `${currency === "USD" ? "$" : `${currency} `}${amount.toLocaleString(
    "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  )}`;

/**
 * Display fallback for quote lines saved before `lineAmount` was stamped:
 * convert the price into the quantity's unit before multiplying, exactly like
 * the server does. A raw price × quantity multiply is only safe when the two
 * units already agree — a $315/piece sheet quoted for 10 SF must not render
 * as $3,150. Null means the line genuinely has no computable amount, which is
 * shown as an em dash (matching the server's itemsMissingPrice handling).
 */
const lineAmountFallback = (item: CartItem): number | null => {
  const quantity = item.quantityRequired?.value || 0;
  if (typeof item.lineAmount === "number") return item.lineAmount;
  const converted = convertPriceValueAndUnit(
    item.product,
    item.quantityRequired?.unit || "",
  );
  if (converted?.value) return converted.value * quantity;
  const priceUnit = canonicalizePriceUnit(item.product?.price?.unit);
  const quantityUnit = canonicalizePriceUnit(item.quantityRequired?.unit);
  if (item.product?.price?.value && (!quantityUnit || priceUnit === quantityUnit)) {
    return item.product.price.value * quantity;
  }
  return null;
};

const day = (iso: string) => (iso ? iso.slice(0, 10) : "");

/** "2026-08-18, 3:15 PM" in the reader's own timezone. */
const dayTime = (iso: string) =>
  iso
    ? `${iso.slice(0, 10)}, ${new Date(iso).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })}`
    : "";

const expired = (quote: { validUntil: string }) =>
  Boolean(quote.validUntil) && new Date(quote.validUntil) < new Date();

/** Newest revision first, with the rest of its family behind it. */
const byQuoteNumber = (quotes: QuoteSummary[]) => {
  const families = new Map<string, QuoteSummary[]>();
  for (const quote of quotes) {
    const family = families.get(quote.quoteNumber) || [];
    family.push(quote);
    families.set(quote.quoteNumber, family);
  }
  return Array.from(families.values()).map((family) =>
    [...family].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
  );
};

const Status = ({ quote }: { quote: QuoteSummary }) => {
  if (quote.orderId) return <span className="text-green-700">Ordered</span>;
  if (quote.supersededBy)
    return <span className="text-gray-500">Replaced by a newer version</span>;
  if (expired(quote))
    return <span className="text-amber-700">Prices have lapsed</span>;
  if (quote.status === "sent")
    return <span className="text-gray-600">Emailed</span>;
  return <span className="text-gray-600">Saved</span>;
};

export default function MyQuotes() {
  const navigate = useNavigate();
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const [quotes, setQuotes] = useState<QuoteSummary[] | null>(null);
  const [asGuest, setAsGuest] = useState(false);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState("");
  const [deleting, setDeleting] = useState("");

  // Deleting removes the quote and every revision behind it, so the whole
  // family leaves the list at once. Ordered quotes are refused by the server.
  const remove = async (family: QuoteSummary[]) => {
    const newest = family[0];
    const label =
      family.length > 1
        ? `${newest.quoteNumber} and its ${family.length} versions`
        : newest.reference;
    if (!window.confirm(`Delete quote ${label}? This cannot be undone.`)) {
      return;
    }
    setDeleting(newest.id);
    setError("");
    try {
      await deleteQuote(newest.id);
      setQuotes(
        (previous) =>
          previous?.filter(
            (quote) => quote.quoteNumber !== newest.quoteNumber,
          ) ?? previous,
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setDeleting("");
    }
  };

  // Editing needs the quote's lines, which the list does not carry.
  const edit = async (id: string) => {
    setOpening(id);
    try {
      reopenInCart(await loadQuote(id));
      navigate("/cart");
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setOpening("");
    }
  };

  // Kono operators: /quotes?scope=all lists every customer's quotes.
  const [searchParams] = useSearchParams();
  const scope: "mine" | "all" = searchParams.get("scope") === "all" ? "all" : "mine";

  const load = useCallback(async () => {
    setError("");
    try {
      const data = await listQuotes(scope);
      setQuotes(data.quotes);
      setAsGuest(data.asGuest);
    } catch (e) {
      setError(messageOf(e));
      setQuotes([]);
    }
  }, [scope]);

  useEffect(() => {
    if (authIsLoading) return;
    void load();
  }, [authIsLoading, authUser, load]);

  const families = useMemo(() => byQuoteNumber(quotes || []), [quotes]);

  return (
    <div className="mx-auto mt-8 mb-16 max-w-4xl px-4">
      <h1 className="text-xl font-semibold text-[#00062e]">
        {scope === "all" ? "All quotes (Kono team)" : "My quotes"}
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-gray-600">
        Quotes you have taken on the material in your cart, priced and dated, to
        take through your own approvals — and quotes sent or shared to your
        email. Each one can be reopened and changed — which saves a new version
        rather than replacing the one you already sent — downloaded as a PDF, or
        emailed again.
      </p>
      {quotes?.some((q) => q.byCoworker) && (
        <p className="mt-1 max-w-2xl text-sm text-gray-600">
          Coworkers at{" "}
          <span className="font-medium text-[#00062e]">
            {quotes.find((q) => q.companyDomain)?.companyDomain}
          </span>{" "}
          share these quotes: everyone at your company sees, edits and buys from
          the same list, and each quote records who did what and when.
        </p>
      )}
      {asGuest && (
        <p className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
          These are held against this browser as a{" "}
          <span className="font-medium">Guest</span>.{" "}
          <Link className="text-blue-700 hover:underline" to="/signin">
            Sign in
          </Link>{" "}
          and they move to your account, where clearing cookies cannot lose
          them.
        </p>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {quotes === null && (
        <p className="mt-4 text-sm text-gray-500">Loading your quotes...</p>
      )}
      {quotes !== null && quotes.length === 0 && !error && (
        <p className="mt-4 text-sm text-gray-600">
          You have not saved a quote yet. Add material to the{" "}
          <Link className="text-blue-700 hover:underline" to="/cart">
            cart
          </Link>{" "}
          and choose Get a quote.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-3">
        {families.map((family) => {
          const [current, ...older] = family;
          return (
            <div
              key={current.quoteNumber}
              className="rounded-xl border border-gray-200 p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <Link
                  className="font-medium text-blue-700 hover:underline"
                  to={`/quotes/${current.id}`}
                >
                  {current.reference}
                </Link>
                <span className="font-medium">
                  {money(current.totalPrice, current.currency)}
                </span>
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {day(current.createdAt)} · {current.itemCount}{" "}
                {current.itemCount === 1 ? "item" : "items"} · holds until{" "}
                {day(current.validUntil)} · <Status quote={current} />
                {current.betterPrice && <BetterPriceBadge flag={current.betterPrice} className="ml-2" />}
              </div>
              {current.sentBy && (
                <div className="mt-1 text-sm text-gray-600">
                  Sent to you by{" "}
                  <span className="font-medium text-[#00062e]">
                    {current.sentBy}
                  </span>{" "}
                  on {dayTime(current.sentAt || "")}
                </div>
              )}
              {current.byCoworker && current.createdBy && (
                <div className="mt-1 text-sm text-gray-600">
                  Created by{" "}
                  <span className="font-medium text-[#00062e]">
                    {actorLabel(current.createdBy, authUser?.email)}
                  </span>{" "}
                  ({current.createdBy.email})
                </div>
              )}
              <ActivityTrail
                className="mt-1"
                activity={current.activity}
                viewerEmail={authUser?.email}
              />
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                <Link
                  className="text-blue-700 hover:underline"
                  to={`/quotes/${current.id}`}
                >
                  Open it
                </Link>
                <button
                  type="button"
                  className="text-blue-700 hover:underline disabled:text-gray-400"
                  onClick={() => void edit(current.id)}
                  disabled={opening === current.id}
                >
                  {opening === current.id ? "Opening..." : "Edit it"}
                </button>
                <button
                  type="button"
                  className="text-blue-700 hover:underline"
                  onClick={() =>
                    void downloadQuotePdf({
                      id: current.id,
                      reference: current.reference,
                    })
                  }
                >
                  Download the PDF
                </button>
                {!current.orderId && (
                  <button
                    type="button"
                    className="text-red-600 hover:underline disabled:text-gray-400"
                    onClick={() => void remove(family)}
                    disabled={deleting === current.id}
                  >
                    {deleting === current.id ? "Deleting..." : "Delete it"}
                  </button>
                )}
              </div>
              {older.length > 0 && (
                <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-500">
                  Earlier versions:{" "}
                  {older.map((version, index) => (
                    <span key={version.id}>
                      {index > 0 && ", "}
                      <Link
                        className="text-blue-700 hover:underline"
                        to={`/quotes/${version.id}`}
                      >
                        {version.reference}
                      </Link>{" "}
                      ({day(version.createdAt)})
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Sharing, on the quote itself: copy the link, or grant more addresses access
 * and email them the link. The customer on the quote and any kono.ai account
 * can already open the link as is — a colleague can review it, edit it (a new
 * version), or send it on to the customer. Anyone else must be shared first,
 * then signs in with that email to see the quote, purchase, or convert to PO.
 */
function ShareQuoteSection({
  quote,
  isKonoTeam,
}: {
  quote: Quote;
  isKonoTeam: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [sharedWith, setSharedWith] = useState<string[]>(
    quote.sharedWithEmails || [],
  );
  const [input, setInput] = useState("");
  const [sharing, setSharing] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const url = `${window.location.origin}/quotes/${quote.id}`;

  const share = async () => {
    const emails = input
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (!emails.length) return;
    const invalid = emails.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid.length) {
      setError(`Not an email address: ${invalid.join(", ")}`);
      return;
    }
    setSharing(true);
    setError("");
    setNotice("");
    try {
      const result = await shareQuote(quote.id, emails);
      setSharedWith(result.sharedWithEmails);
      setInput("");
      setNotice(
        result.emailed
          ? "Shared — each new address was emailed the link."
          : "Those addresses already had access.",
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <h2 className="text-sm font-semibold text-[#00062e]">Share this quote</h2>
      <p className="mt-1 text-xs text-gray-600">
        {isKonoTeam
          ? "Any kono.ai account can open this link to review the quote, edit it (which saves a new version), or send it on. The customer it is addressed to (" +
            (quote.customer.email || "no email on the quote") +
            "), and anyone signed in with the same company domain, can open it to pay or convert it to a PO. To let anyone else in, share their email below."
          : "The people this quote is shared with can open this link, signed in with their email, to review it, pay, or convert it to a PO."}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-100"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              /* clipboard denied; the text beside it is selectable */
            }
          }}
        >
          {copied ? "Link copied" : "Copy quote link"}
        </button>
        <span className="select-all break-all text-xs text-gray-500">
          {url}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          className="w-72 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs text-[#00062e] focus:border-teal-500 focus:outline-none"
          placeholder="Share by email: colleague@kono.ai, name@company.com"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError("");
            setNotice("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") void share();
          }}
        />
        <button
          type="button"
          className="rounded-full bg-[#00062e] px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900 disabled:opacity-50"
          onClick={() => void share()}
          disabled={sharing || !input.trim()}
        >
          {sharing ? "Sharing…" : "Share by email"}
        </button>
      </div>
      {notice && <p className="mt-2 text-xs text-green-700">{notice}</p>}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      {sharedWith.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          Shared with: {sharedWith.join(", ")}
        </p>
      )}
    </div>
  );
}

export function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [config, setConfig] = useState<QuoteFlowConfig | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [converting, setConverting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [freightOpen, setFreightOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const isKono = Boolean(authUser?.email?.endsWith("@kono.ai"));

  // A customer opening a quote link never sees this page: they land straight
  // on checkout — the quote's lines already in the cart, their details filled
  // in, one step from entering card details. This page is the operators' view:
  // only kono.ai reviewers stay here, plus quotes that can no longer be bought
  // (already ordered or superseded), which have nothing to check out.
  useEffect(() => {
    if (!quote || authIsLoading) return;
    if (quote.orderId || quote.supersededBy) return;
    if (authUser?.email?.endsWith("@kono.ai")) return;
    reopenInCart(quote, { revising: false });
    navigate("/cart", { replace: true });
  }, [quote, authIsLoading, authUser, navigate]);

  // Waits for the sign-in to be restored before asking. On a fresh load of this
  // URL — a bookmark, a new tab, the link from a quote email — the token is not
  // there for the first moments, and a quote held against an account is not
  // ours without it. Even after the user is restored, the first request can
  // race the token: it went out anonymous, the server said "not found", and
  // the page kept that error while signed in. So the token is awaited first,
  // and a "not found" is retried once before it is believed.
  useEffect(() => {
    if (!id || authIsLoading) return;
    let cancelled = false;
    const attempt = async (retriesLeft: number) => {
      try {
        if (authUser) await authUser.getIdToken();
        const [loaded, flowConfig] = await Promise.all([
          loadQuote(id),
          loadQuoteFlowConfig(),
        ]);
        if (cancelled) return;
        setQuote(loaded);
        setConfig(flowConfig);
        setError("");
      } catch (e) {
        if (cancelled) return;
        if (retriesLeft > 0 && /not found/i.test(messageOf(e))) {
          setTimeout(() => {
            if (!cancelled) void attempt(retriesLeft - 1);
          }, 2000);
          return;
        }
        setError(messageOf(e));
      }
    };
    setError("");
    void attempt(1);
    return () => {
      cancelled = true;
    };
  }, [id, authIsLoading, authUser]);

  const send = async () => {
    if (!quote) return;
    setBusy("email");
    setError("");
    try {
      await emailQuote(quote.id, quote.customer.email);
      setNotice(`Sent to ${quote.customer.email}.`);
      // The customer holds this version now: further edits make a new one.
      setQuote(await loadQuote(quote.id));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy("");
    }
  };

  const markSent = async () => {
    if (!quote) return;
    const how = window.prompt("How did the customer get this quote? (optional: 'from my mailbox', 'on the phone', ...)", "sent from my mailbox");
    if (how === null) return;
    setBusy("mark-sent");
    setError("");
    try {
      const result = await markQuoteSentToCustomer(quote.id, how);
      setQuote(result.quote);
      setNotice(result.alreadySent ? `${quote.reference} was already sent to the customer.` : `${quote.reference} marked as sent to the customer. Changes from now on make a new version.`);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy("");
    }
  };

  // Operators: does the customer hold THIS version (then edits make a new
  // one)? Mirrors the server's rule (quotes/store.ts customerHasQuote):
  // only what happened to this version counts, not what earlier versions
  // inherited (sentLog, sharedWithEmails).
  const customerEmail = (quote?.customer.email || "").toLowerCase();
  const reached = (e: string) => !e.toLowerCase().endsWith("@kono.ai") || e.toLowerCase() === customerEmail;
  const customerHasIt = Boolean(
    quote &&
      (quote.sentToCustomerAt ||
        quote.orderId ||
        quote.status === "converted" ||
        (quote.emailedTo || []).some(reached) ||
        (quote.activity || []).some((entry) => entry.action === "shared" && entry.at >= (quote.createdAt || "") && (entry.detail.match(/[^\s,<>]+@[^\s,<>]+/g) || []).some(reached)) ||
        (quote.createdBy?.email && !quote.createdBy.email.toLowerCase().endsWith("@kono.ai"))),
  );

  const currency = quote?.totals?.currency || "USD";

  return (
    <div className="mx-auto mt-8 mb-16 max-w-4xl px-4">
      <Link className="text-sm text-blue-700 hover:underline" to="/quotes">
        ← My quotes
      </Link>
      {/* A quote link is private, and the server answers strangers with "not
          found" rather than confirming the quote exists. From the browser we
          know more than the server says: signed out, the fix is signing in —
          say so instead of showing a dead end. Signing in re-runs the load. */}
      {error && /not found/i.test(error) && !authUser ? (
        <div className="mt-4 max-w-xl rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-gray-700">
          <p className="font-medium text-[#00062e]">
            This quote is private — sign in to open it.
          </p>
          <p className="mt-1">
            <Link className="text-blue-700 hover:underline" to="/signin">
              Sign in
            </Link>{" "}
            with the email this quote was shared with — the customer email on
            the quote, a kono.ai account, or an address it was explicitly
            shared to — and it will open right here.
          </p>
        </div>
      ) : error && /not found/i.test(error) && authUser ? (
        <div className="mt-4 max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-gray-700">
          <p className="font-medium text-[#00062e]">
            This quote is not available to {authUser.email || "this account"}.
          </p>
          <p className="mt-1">
            Ask whoever sent you the link to share the quote with this exact
            email address (from the quote's Share section), then reload this
            page. Or sign in with the account the quote was addressed or
            shared to.
          </p>
          <button
            type="button"
            className="mt-2 rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-white"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </div>
      ) : (
        error && <p className="mt-3 text-sm text-red-600">{error}</p>
      )}
      {!quote && !error && (
        <p className="mt-4 text-sm text-gray-500">Loading the quote...</p>
      )}

      {quote && (
        <>
          <h1 className="mt-2 text-xl font-semibold text-[#00062e]">
            Quote {quote.reference}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Issued {day(quote.createdAt)} · holds until {day(quote.validUntil)}
            {expired(quote) ? " (lapsed — reopen it for current prices)" : ""}
            {quote.supersededBy
              ? " · a newer version of this quote exists"
              : ""}
          </p>
          {isKono && quote.betterPrice && (
            <p className="mt-1 text-xs">
              <BetterPriceBadge flag={quote.betterPrice} />
            </p>
          )}
          {isKono && !quote.supersededBy && !quote.orderId && (
            <p className="mt-1 text-xs text-amber-900">
              {customerHasIt
                ? `Internal: the customer holds this version${quote.sentToCustomerAt ? ` (sent ${day(quote.sentToCustomerAt)}${quote.sentToCustomerBy ? ` by ${quote.sentToCustomerBy}` : ""})` : ""}. Changes create a new version.`
                : "Internal: not sent to the customer yet. Changes update this same version; once you email it or mark it as sent, changes create a new version."}
            </p>
          )}
          {quote.supersededBy && (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              This version was replaced.{" "}
              <Link className="font-medium text-blue-700 hover:underline" to={`/quotes/${quote.supersededBy}`}>
                Open the updated quote
              </Link>{" "}
              for current items, prices and lead times.
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Prices and lead times on this quote are valid until that date. If
            you convert it to a purchase order or check out by then, Kono
            maintains the quoted terms; after that date Kono cannot guarantee
            the quoted price and lead time.
          </p>
          {notice && <p className="mt-2 text-sm text-green-700">{notice}</p>}

          <table className="mt-5 min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th className="py-2 pr-4">Item</th>
                <th className="py-2 pr-4">Quantity</th>
                <th className="py-2 pr-4">Ship to</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {quote.cartItems.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-2 pr-4">
                    {item.product?.summary || "Item"}
                    {item.millCertRequired && (
                      <div className="text-xs text-gray-500">
                        Mill certificate required
                      </div>
                    )}
                    {(item.alternatives || []).map((alt) => (
                      <div key={alt.product?.id} className="text-xs text-amber-800">
                        Also offered: {alt.product?.summary || "option"}
                        {typeof alt.lineAmount === "number"
                          ? ` - ${money(alt.lineAmount, currency)}`
                          : ""}
                      </div>
                    ))}
                  </td>
                  <td className="py-2 pr-4">
                    {item.quantityRequiredString ||
                      (item.quantityRequired
                        ? `${item.quantityRequired.value} ${item.quantityRequired.unit}`
                        : "")}
                  </td>
                  <td className="py-2 pr-4 text-gray-600">
                    {[
                      item.deliveryAddress?.city,
                      item.deliveryAddress?.stateOrProvinceCode,
                      item.deliveryAddress?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </td>
                  <td className="py-2 text-right">
                    {/* Server-computed: price unit converted to the quantity
                        unit (per-lb price x piece weight), so lines sum to the
                        subtotal. Unit-aware fallback for older documents. */}
                    {(() => {
                      const amount = lineAmountFallback(item);
                      return amount === null ? "—" : money(amount, currency);
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3 flex flex-col items-end gap-1 text-sm">
            <div>
              Subtotal:{" "}
              {money(
                quote.totals.productSubtotalBeforeDiscount ||
                  quote.totals.productSubtotal,
                currency,
              )}
            </div>
            {quote.totals.volumeDiscount && (
              <div>
                Volume discount ({quote.totals.volumeDiscount.discountPercent}%
                on qualifying items over $
                {quote.totals.volumeDiscount.threshold.toLocaleString()}
                ): −
                {money(quote.totals.volumeDiscount.discountAmount, currency)}
              </div>
            )}
            {quote.includeShipping && (
              <div>
                Shipping:{" "}
                {quote.totals.shippingTotal === 0
                  ? "Free"
                  : money(quote.totals.shippingTotal, currency)}
              </div>
            )}
            <div className="text-base font-semibold">
              Total: {money(quote.totals.totalPrice, currency)}
            </div>
          </div>

          {quote.termsText && (
            <p className="mt-4 whitespace-pre-line text-xs text-gray-500">
              {quote.termsText}
            </p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="rounded-full bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-slate-900"
              onClick={() => void downloadQuotePdf(quote)}
            >
              Download the PDF
            </button>
            {/* Operators edit the email first (OperatorEmailPanel below); customers get the standard one at once. */}
            {config?.quote.emailPdf && isKono && (
              <button
                type="button"
                className={`rounded-full border px-4 py-2 text-sm ${composeOpen ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-gray-300 hover:bg-gray-50"}`}
                onClick={() => {
                  setComposeOpen((open) => !open);
                  window.setTimeout(() => document.getElementById("operator-email-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                }}
                title="Opens the email to edit (to, copies, subject, body) before it goes, with the PDF attached"
              >
                {composeOpen ? "Close the email" : `Email it to ${quote.customer.email || "the customer"} — edit first`}
              </button>
            )}
            {config?.quote.emailPdf && !isKono && (
              <button
                type="button"
                className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => void send()}
                disabled={busy === "email"}
              >
                {busy === "email"
                  ? "Sending..."
                  : `Email it to ${quote.customer.email || "me"}`}
              </button>
            )}
            {!quote.supersededBy && !quote.orderId && (
              <button
                type="button"
                className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => {
                  reopenInCart(quote, { revising: false });
                  navigate("/cart");
                }}
              >
                Buy it — check out from the cart
              </button>
            )}
            {/* Kono operators edit the quote in place (any field, any line,
                the vendor, the freight); customers re-open it in the cart. */}
            {isKono && !quote.supersededBy && !quote.orderId && (
              <button
                type="button"
                className="rounded-full border border-amber-400 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100"
                onClick={() => setEditing((open) => !open)}
              >
                {editing ? "Close the editor" : "Edit this quote — price, items, vendor, freight"}
              </button>
            )}
            {isKono && !quote.supersededBy && (
              <button
                type="button"
                className="rounded-full border border-sky-400 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-900 hover:bg-sky-100"
                onClick={() => setFreightOpen((open) => !open)}
              >
                {freightOpen ? "Close freight" : "Freight — addresses, WWEX cost, who pays"}
              </button>
            )}
            {isKono && !quote.supersededBy && !quote.orderId && !customerHasIt && (
              <button
                type="button"
                className="rounded-full border border-amber-400 px-4 py-2 text-sm text-amber-900 hover:bg-amber-50"
                onClick={() => void markSent()}
                disabled={busy === "mark-sent"}
                title="You sent it from your own mailbox or gave it on the phone: record that the customer has this version."
              >
                {busy === "mark-sent" ? "Marking..." : "Mark as sent to customer"}
              </button>
            )}
            {!isKono && config?.quote.allowRevisions && !quote.supersededBy && (
              <button
                type="button"
                className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => {
                  reopenInCart(quote);
                  navigate("/cart");
                }}
              >
                Edit it — saves a new version
              </button>
            )}
            {config?.po.show && !quote.supersededBy && !quote.orderId && (
              <button
                type="button"
                className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => setConverting(true)}
              >
                Convert to a purchase order
              </button>
            )}
            {/* Kono only: the customer never sees this page — their link lands
                straight on checkout. This opens that exact screen, with the
                customer's name, company and email in the fields. */}
            {authUser?.email?.endsWith("@kono.ai") &&
              !quote.supersededBy &&
              !quote.orderId && (
                <button
                  type="button"
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={() => {
                    reopenInCart(quote, { revising: false });
                    navigate("/cart");
                  }}
                >
                  See as the customer will see
                </button>
              )}
          </div>
          {config?.po.show && (
            <p className="mt-3 max-w-2xl text-xs text-gray-500">
              Buying puts these lines in the cart to pay right away — by card
              or wire — no PO needed. Converting instead holds these lines and
              this price as an order under our own reference, and is where you
              enter the PO number your own system issued and attach your PO
              document; nothing is charged until you choose how to pay.
            </p>
          )}

          {isKono && editing && (
            <OperatorQuoteEditor
              quote={quote}
              onClose={() => setEditing(false)}
              onOpenFreight={() => {
                setFreightOpen(true);
                window.setTimeout(() => document.getElementById("operator-freight-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
              }}
              onSaved={(next, changes, revised) => {
                setEditing(false);
                setNotice(revised ? `Saved as ${next.reference}: ${changes.join("; ")}` : `${next.reference} updated (same version, not sent to the customer yet): ${changes.join("; ")}`);
                if (revised) navigate(`/quotes/${next.id}`);
                else setQuote(next);
              }}
            />
          )}

          {isKono && freightOpen && (
            <OperatorFreightPanel
              quote={quote}
              onSaved={(next, revised, summary) => {
                setFreightOpen(false);
                setNotice(revised ? `Saved as ${next.reference}: ${summary}` : `${next.reference} updated: ${summary}`);
                if (revised) navigate(`/quotes/${next.id}`);
                else setQuote(next);
              }}
            />
          )}

          {isKono && (
            <OperatorEmailPanel
              quote={quote}
              composeOpen={composeOpen}
              onClose={() => setComposeOpen(false)}
              onSent={(to) => {
                setComposeOpen(false);
                setNotice(`Sent to ${to}.`);
                // The customer holds this version now: further edits make a new one.
                void loadQuote(quote.id).then(setQuote).catch(() => undefined);
              }}
            />
          )}

          {quote.activity && quote.activity.length > 0 && (
            <div className="mt-4 rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-[#00062e]">
                Who did what
              </h2>
              {quote.createdBy && (
                <p className="mt-1 text-xs text-gray-600">
                  Created by {actorLabel(quote.createdBy, authUser?.email)} (
                  {quote.createdBy.email})
                  {quote.companyDomain
                    ? `; shared with coworkers at ${quote.companyDomain}`
                    : ""}
                </p>
              )}
              <ActivityTrail
                className="mt-2"
                activity={quote.activity}
                viewerEmail={authUser?.email}
                startOpen
              />
            </div>
          )}

          {authUser?.email?.endsWith("@kono.ai") && (
            <QuoteSourcingSection quoteId={quote.id} currency={currency} />
          )}

          <ShareQuoteSection
            quote={quote}
            isKonoTeam={Boolean(authUser?.email?.endsWith("@kono.ai"))}
          />

          {config && (
            <ConvertToPoDialog
              open={converting}
              onClose={() => setConverting(false)}
              quote={quote}
              config={config}
              signedIn={Boolean(authUser)}
              onRaised={(po) => {
                setConverting(false);
                navigate(`/purchase-orders/${po.id}`);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
